/**
 * Markdown — render trusted markdown source to sanitized HTML.
 *
 * Pipeline: `marked` parses to HTML → DOMPurify strips anything dangerous →
 * `dangerouslySetInnerHTML` renders. Both deps are already in the bundle.
 *
 * Used for chat bubbles and Job artifact previews. Don't expand the surface
 * here without thinking about the threat model — both sources are
 * tenant-authored, so unrendered HTML/scripts must stay out.
 */

import { useMemo, type HTMLAttributes } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import styles from './Markdown.module.css';

export interface MarkdownProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Raw markdown source. Empty string renders nothing. */
  content: string;
}

export function Markdown({ content, className, ...rest }: MarkdownProps) {
  const html = useMemo(() => {
    if (!content) return '';
    const rendered = marked.parse(content, { async: false });
    return DOMPurify.sanitize(rendered);
  }, [content]);

  if (!html) return null;

  const composedClassName = [styles.markdown, className ?? ''].filter(Boolean).join(' ');
  return <div className={composedClassName} dangerouslySetInnerHTML={{ __html: html }} {...rest} />;
}
